/**
 * Dex image analysis pipeline — Push 3 / v1.4.3 b3.
 *
 * The user's advanced Pokédex OCR + image-analysis pipeline for continuous
 * screenshots / screen recordings of the Pokémon GO Pokédex 4×4 grid.
 *
 * This module is intentionally PURE and DOM-free: every function takes raw
 * RGBA byte buffers (the exact layout returned by canvas `getImageData`
 * AND by the Node test harness), so the same logic that runs in the live
 * iOS broadcast path is exercised deterministically by the unit tests with
 * synthetic fixtures. The browser/iOS glue (canvas crop → RGBA, asset
 * persistence) lives in `dexCropCapture.ts`; this file owns the geometry,
 * the colour-based registration classifier, the region normaliser, the
 * dedupe/session-JSON contract, and the motion/throttle heuristics.
 *
 * Hard contracts (mirroring the existing Pokédex-only barrier):
 *   - We emit ONLY Dex-state-shaped data: { dex_number, is_registered,
 *     image_asset_path }. Never CP/IV/instance/nickname.
 *   - `dex_number` (4-digit zero-padded) is the absolute dedupe anchor for
 *     a live session.
 *   - We never fabricate entries: only cells where the sprite AND the
 *     bottom number are fully visible are emitted.
 *   - A crop/asset is produced ONLY for `is_registered: true` cells, and
 *     only when the frame is stable and the cell is near the middle.
 *
 * RGBA layout: 4 bytes/pixel, row-major, top-down. width*height*4 bytes.
 */

import { avgRgbFromRgba, phash16FromRgba, phashHamming } from "./ocrPixelOps";
import { dexIdToRegion } from "./dexIdToRegion";

// ── Region tags ─────────────────────────────────────────────────────────────

/**
 * Canonical upper-cased region tags for the session JSON.
 *
 * Build 13a — "UNIDENTIFIED" (Meltan/Melmetal, 808-809) is its own tag to
 * mirror Pokémon GO's sorting, and the old header-text *detector*
 * (normalizeRegionTag / resolveSessionRegion) is gone: region is now
 * derived deterministically from the dex id via [[dexIdToRegion]].
 */
export type DexRegionTag =
  | "KANTO" | "JOHTO" | "HOENN" | "SINNOH" | "UNOVA"
  | "KALOS" | "ALOLA" | "UNIDENTIFIED" | "GALAR" | "HISUI" | "PALDEA" | "UNKNOWN";

// ── Dex number padding ──────────────────────────────────────────────────────

/**
 * Pad a parsed dex number to the 4-digit string the JSON contract and the
 * crop path require. Accepts a number or a numeric string; returns null on
 * anything that isn't a plausible national-dex number (1..1100).
 */
export function padDexNumber(value: number | string | null | undefined): string | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n) || n < 1 || n > 1100) return null;
  return String(n).padStart(4, "0");
}

// ── 4×4 grid geometry + cell visibility ─────────────────────────────────────

/**
 * Normalised geometry of the Pokémon GO Pokédex 4×4 grid, calibrated to
 * the existing live-scan references (the same anchors `dexLiveFrameAdapter`
 * uses: grid starts ~0.17, ~0.16 per row, 4 columns). Values are fractions
 * of the full frame width/height (0-1).
 *
 * The grid is 4 columns wide. Up to 4 rows are visible; while scrolling the
 * top and bottom rows can be clipped, so we compute each row's vertical
 * extent and reject any row whose sprite+number band would fall outside the
 * trusted scan window.
 */
export interface DexGridGeometry {
  /** Left edge of column 0 (fraction of width). */
  gridLeft: number;
  /** Right edge of column 3 (fraction of width). */
  gridRight: number;
  /** Top of the first (possibly clipped) row (fraction of height). */
  gridTop: number;
  /** Per-row height (fraction of height). */
  rowHeight: number;
  /** Number of columns (always 4 for PoGo). */
  columns: number;
  /** Max rows we will consider in one frame. */
  maxRows: number;
  /**
   * Cells fully above this y (their bottom number) are trustworthy; the
   * bottom carousel / region FAB live below it. Fraction of height.
   */
  bottomTrustLimit: number;
  /**
   * Top header band. Cells whose top falls above this are clipped by the
   * region header / status bar. Fraction of height.
   */
  topTrustLimit: number;
}

export const DEFAULT_DEX_GRID_GEOMETRY: DexGridGeometry = {
  gridLeft: 0.02,
  gridRight: 0.98,
  gridTop: 0.17,
  rowHeight: 0.16,
  columns: 4,
  maxRows: 4,
  bottomTrustLimit: 0.86,
  topTrustLimit: 0.15,
};

/** A single isolated cell rectangle, in fractional (0-1) coordinates. */
export interface DexCellRect {
  rowIndex: number;
  colIndex: number;
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
  /** Centre of the cell (fraction). */
  cxPct: number;
  cyPct: number;
}

/**
 * Compute the rectangle for a (row, col) cell. Pure geometry; does not
 * decide visibility.
 */
export function cellRect(
  row: number,
  col: number,
  geo: DexGridGeometry = DEFAULT_DEX_GRID_GEOMETRY,
): DexCellRect {
  const gridW = geo.gridRight - geo.gridLeft;
  const colW = gridW / geo.columns;
  const xPct = geo.gridLeft + col * colW;
  const yPct = geo.gridTop + row * geo.rowHeight;
  return {
    rowIndex: row,
    colIndex: col,
    xPct,
    yPct,
    wPct: colW,
    hPct: geo.rowHeight,
    cxPct: xPct + colW / 2,
    cyPct: yPct + geo.rowHeight / 2,
  };
}

/**
 * Decide whether a cell is FULLY visible — both the sprite area and the
 * bottom number must sit inside the trusted scan window. Rejects:
 *   - top rows clipped by the region header / status bar,
 *   - bottom rows whose number band would overlap the carousel / FAB,
 *   - cells outside the horizontal grid extent.
 */
export function isCellFullyVisible(
  rect: DexCellRect,
  geo: DexGridGeometry = DEFAULT_DEX_GRID_GEOMETRY,
): boolean {
  const cellTop = rect.yPct;
  const cellBottom = rect.yPct + rect.hPct;
  if (cellTop < geo.topTrustLimit) return false;
  if (cellBottom > geo.bottomTrustLimit) return false;
  if (rect.xPct < geo.gridLeft - 1e-6) return false;
  if (rect.xPct + rect.wPct > geo.gridRight + 1e-6) return false;
  return true;
}

/**
 * Enumerate the fully-visible cell rectangles for a frame given a vertical
 * scroll offset. `scrollPhase` 0 means the grid sits at the canonical
 * gridTop; positive values shift the grid up (rows scrolled past the top).
 * We clamp to plausible offsets so an out-of-range value can't fabricate
 * phantom rows.
 */
export function visibleCells(
  geo: DexGridGeometry = DEFAULT_DEX_GRID_GEOMETRY,
  scrollPhase = 0,
): DexCellRect[] {
  const shifted: DexGridGeometry = {
    ...geo,
    gridTop: geo.gridTop - clamp(scrollPhase, -geo.rowHeight, geo.rowHeight) ,
  };
  const out: DexCellRect[] = [];
  for (let row = 0; row < geo.maxRows; row++) {
    for (let col = 0; col < geo.columns; col++) {
      const rect = cellRect(row, col, shifted);
      if (isCellFullyVisible(rect, geo)) out.push(rect);
    }
  }
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// ── Registration status classification (colour analysis) ────────────────────

export interface RegistrationClassification {
  isRegistered: boolean;
  /** 0-1, fraction of foreground pixels that carry identifying colour. */
  colorfulness: number;
  /** 0-1, fraction of foreground pixels that are near-grey. */
  greyness: number;
  /** Foreground pixels sampled (excludes background/transparent-like). */
  foregroundPixels: number;
  /** 0-1 confidence in the classification. */
  confidence: number;
}

/**
 * Background detection: PoGo Pokédex cells render the sprite over a pale
 * card (near-white / very light). Silhouettes are a solid mid-grey or
 * black filled shape. We treat "background" as near-white / very-light
 * card pixels and very low-alpha pixels (transparent-like sprite cutouts),
 * leaving the actual sprite/silhouette as foreground.
 */
function isBackgroundPixel(r: number, g: number, b: number, a: number): boolean {
  if (a < 24) return true; // transparent-like
  // Near-white card background.
  const min = Math.min(r, g, b);
  if (min > 222) return true;
  return false;
}

/**
 * Is a foreground pixel "grey/monochrome" (a silhouette) vs carrying an
 * identifying hue? Greys have low chroma; silhouettes are also often quite
 * dark. We measure chroma = (max-min) and call low-chroma pixels grey.
 */
function isGreyPixel(r: number, g: number, b: number): boolean {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const chroma = max - min;
  return chroma <= 22;
}

/**
 * Classify a sprite crop's RGBA as registered (vibrant palette) vs an
 * unregistered silhouette (solid grey/black with no identifying colour).
 *
 * Handles both the high-quality MasterDex-like assets/silhouettes and the
 * Pokémon GO grey grid silhouettes:
 *   - We discard near-white card background + transparent-like pixels.
 *   - Of the remaining foreground, we measure how many carry chroma.
 *   - A diverse/vibrant palette → registered. A near-uniform grey/black
 *     fill → not registered.
 */
export function classifyRegistrationFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): RegistrationClassification {
  if (width < 2 || height < 2) {
    return { isRegistered: false, colorfulness: 0, greyness: 0, foregroundPixels: 0, confidence: 0 };
  }
  let fg = 0;
  let colorful = 0;
  let grey = 0;
  // Track distinct coarse hues to reward palette diversity (a vibrant
  // sprite spans several hue buckets; a tinted-but-monochrome shape does not).
  const hueBuckets = new Uint8Array(12);
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (isBackgroundPixel(r, g, b, a)) continue;
    fg++;
    if (isGreyPixel(r, g, b)) {
      grey++;
    } else {
      colorful++;
      const h = rgbHue(r, g, b);
      if (h >= 0) hueBuckets[Math.min(11, Math.floor(h / 30))] = 1;
    }
  }
  if (fg === 0) {
    // Entire crop was background — can't tell. Treat as unregistered with
    // zero confidence so the caller can skip / retry.
    return { isRegistered: false, colorfulness: 0, greyness: 0, foregroundPixels: 0, confidence: 0 };
  }
  const colorfulness = colorful / fg;
  const greyness = grey / fg;
  let distinctHues = 0;
  for (let i = 0; i < 12; i++) distinctHues += hueBuckets[i];

  // Registered when a meaningful fraction of the silhouette carries colour
  // AND the palette spans more than a single hue bucket (rejects a sprite
  // that is uniformly one tinted colour, which silhouettes sometimes are
  // after compression). Threshold tuned against synthetic fixtures and the
  // grey-silhouette case.
  const isRegistered = colorfulness >= 0.18 && distinctHues >= 2;

  // Confidence: strong when clearly colourful or clearly grey; weak in the
  // ambiguous middle band.
  const margin = Math.abs(colorfulness - 0.18);
  const confidence = clamp(0.5 + margin * 2, 0, 1);

  return {
    isRegistered,
    colorfulness,
    greyness,
    foregroundPixels: fg,
    confidence,
  };
}

/** Hue 0-360 for a pixel, or -1 for achromatic. */
function rgbHue(r: number, g: number, b: number): number {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  if (d < 1e-6) return -1;
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h;
}

// ── Crop asset path naming ──────────────────────────────────────────────────

/**
 * Region-safe region tag for a dex number. The National Dex number alone
 * determines which region an entry belongs to (Raticate #20 is always
 * Kanto), so deriving the asset-path region from the number — instead of
 * the session's sticky region — makes crop filenames immune to stale region
 * leakage across frames. This is the fix for a KANTO session emitting
 * `paldea_0020.png` after a prior Paldea scan left the session region sticky.
 *
 * Returns "UNKNOWN" only when the number is outside the known roster (e.g. a
 * misread), letting the caller fall back to the session region for those.
 */
export function regionTagForDexNumber(dexNumber: number | string): DexRegionTag {
  const padded = padDexNumber(dexNumber);
  if (!padded) return "UNKNOWN";
  return dexIdToRegion(parseInt(padded, 10)).toUpperCase() as DexRegionTag;
}

/**
 * Build the crop asset path for a registered cell. Format:
 *   `/assets/<region>_<dex4>.png`  e.g. `/assets/kanto_0125.png`
 *
 * Region safety (Push 2 / v1.4.4 b3): the region prefix is derived from the
 * DEX NUMBER first (via [[regionTagForDexNumber]]), not the session's sticky
 * region, so a stale prior region can never leak into a crop path. The passed
 * `region` is only used as a fallback when the number is out of roster range.
 * Dex number is zero-padded to 4. Returns null when the dex number is
 * implausible (so we never emit a malformed path).
 */
export function cropAssetPath(region: DexRegionTag, dexNumber: number | string): string | null {
  const padded = padDexNumber(dexNumber);
  if (!padded) return null;
  const reg = regionForAsset(region, dexNumber);
  return `/assets/${reg}_${padded}.png`;
}

/** Bare filename form (`kanto_0125.png`) for storage backends keyed by name. */
export function cropAssetFilename(region: DexRegionTag, dexNumber: number | string): string | null {
  const padded = padDexNumber(dexNumber);
  if (!padded) return null;
  return `${regionForAsset(region, dexNumber)}_${padded}.png`;
}

/**
 * Resolve the lower-cased region prefix for an asset name. Prefers the
 * dex-number-derived region (deterministic, leak-proof) and only falls back
 * to the supplied session region when the number is out of roster range.
 */
function regionForAsset(sessionRegion: DexRegionTag, dexNumber: number | string): string {
  const derived = regionTagForDexNumber(dexNumber);
  const chosen = derived !== "UNKNOWN" ? derived : sessionRegion;
  return chosen.toLowerCase();
}

// ── Structured session JSON ─────────────────────────────────────────────────

/** One Pokémon entry in the session_update JSON. */
export interface SessionPokemonEntry {
  dex_number: string;
  is_registered: boolean;
  image_asset_path: string | null;
}

export interface SessionUpdate {
  region: DexRegionTag;
  entries_scanned: number;
  new_registrations: number;
  pokemon: SessionPokemonEntry[];
}

/** The exact wire shape the user specified. */
export interface SessionUpdateEnvelope {
  session_update: SessionUpdate;
}

/**
 * An internal observation the live pipeline produces for one fully-visible
 * cell on one frame, before dedupe.
 */
export interface DexCellObservation {
  dexNumber: number;
  isRegistered: boolean;
  /** Path for the saved crop, when one was/will be produced. */
  imageAssetPath: string | null;
  /** True when this is the first time the session saw this dex number. */
  isNew?: boolean;
}

// ── Session dedupe + JSON assembly ──────────────────────────────────────────

export interface DexSessionDiagnostics {
  entriesScanned: number;
  newRegistrations: number;
  /** Observations skipped because the dex number was already in the session. */
  dedupedSkipped: number;
  /** Frames skipped by the motion/throttle gate. */
  motionSkippedFrames: number;
  /** Cells skipped for low confidence / not fully visible. */
  visibilitySkippedCells: number;
  /** Registered cells for which a crop was actually captured. */
  cropsCaptured: number;
  /** Registered cells whose crop could not be persisted (storage limit). */
  cropsUnavailable: number;
}

export function emptySessionDiagnostics(): DexSessionDiagnostics {
  return {
    entriesScanned: 0,
    newRegistrations: 0,
    dedupedSkipped: 0,
    motionSkippedFrames: 0,
    visibilitySkippedCells: 0,
    cropsCaptured: 0,
    cropsUnavailable: 0,
  };
}

/**
 * A live, in-memory dedupe accumulator keyed by 4-digit dex number. This is
 * the session-wide Set the user asked for: a dex number seen on an earlier
 * frame is instantly skipped on later overlapping frames so we never
 * re-crop or re-emit it.
 *
 * `is_registered` can UPGRADE within a session (a silhouette that becomes
 * registered on a later, clearer frame flips false→true) but never
 * downgrades — once we've seen a vibrant sprite for a number we trust it.
 */
export function createDexSession(region: DexRegionTag = "UNKNOWN") {
  const byNumber = new Map<string, SessionPokemonEntry>();
  const diagnostics = emptySessionDiagnostics();
  let sessionRegion = region;

  function setRegion(next: DexRegionTag) {
    // Build 13a — region is derived deterministically from the dex id, so a
    // concrete tag always wins; a bare UNKNOWN (no id resolved this frame)
    // leaves the prior tag intact rather than resetting it.
    if (next !== "UNKNOWN") sessionRegion = next;
  }

  /**
   * Ingest one fully-visible cell observation. Returns whether it produced
   * a NEW registration this call (so the caller can drive crop capture only
   * on first sight). Deduped repeats return false and bump the diag counter.
   */
  function observe(obs: DexCellObservation): { isNew: boolean; entry: SessionPokemonEntry | null } {
    const padded = padDexNumber(obs.dexNumber);
    if (!padded) {
      diagnostics.visibilitySkippedCells += 1;
      return { isNew: false, entry: null };
    }
    diagnostics.entriesScanned += 1;
    const existing = byNumber.get(padded);
    if (existing) {
      // Dedupe: already in session. Allow a false→true registration upgrade.
      if (!existing.is_registered && obs.isRegistered) {
        existing.is_registered = true;
        if (obs.imageAssetPath) existing.image_asset_path = obs.imageAssetPath;
        diagnostics.newRegistrations += 1;
        return { isNew: true, entry: existing };
      }
      diagnostics.dedupedSkipped += 1;
      return { isNew: false, entry: existing };
    }
    const entry: SessionPokemonEntry = {
      dex_number: padded,
      is_registered: obs.isRegistered,
      image_asset_path: obs.isRegistered ? obs.imageAssetPath ?? null : null,
    };
    byNumber.set(padded, entry);
    if (obs.isRegistered) diagnostics.newRegistrations += 1;
    return { isNew: true, entry };
  }

  /** Attach a captured crop path to an already-observed registered entry. */
  function attachCrop(dexNumber: number | string, path: string | null, ok: boolean) {
    const padded = padDexNumber(dexNumber);
    if (!padded) return;
    const entry = byNumber.get(padded);
    if (!entry) return;
    if (ok && path) {
      entry.image_asset_path = path;
      diagnostics.cropsCaptured += 1;
    } else {
      diagnostics.cropsUnavailable += 1;
    }
  }

  function markMotionSkippedFrame() {
    diagnostics.motionSkippedFrames += 1;
  }
  function markVisibilitySkippedCell(n = 1) {
    diagnostics.visibilitySkippedCells += n;
  }

  /** Build the exact session_update JSON envelope from current state. */
  function toJson(): SessionUpdateEnvelope {
    const pokemon = Array.from(byNumber.values()).sort((a, b) =>
      a.dex_number.localeCompare(b.dex_number),
    );
    return {
      session_update: {
        region: sessionRegion,
        entries_scanned: pokemon.length,
        new_registrations: pokemon.filter((p) => p.is_registered).length,
        pokemon,
      },
    };
  }

  return {
    observe,
    attachCrop,
    setRegion,
    markMotionSkippedFrame,
    markVisibilitySkippedCell,
    get region() { return sessionRegion; },
    get size() { return byNumber.size; },
    has(dexNumber: number | string) {
      const p = padDexNumber(dexNumber);
      return p ? byNumber.has(p) : false;
    },
    diagnostics,
    toJson,
  };
}

export type DexSession = ReturnType<typeof createDexSession>;

// ── Frame throttle + motion detection ───────────────────────────────────────

/**
 * Live capture is gated by two cooperating ideas:
 *   1. THROTTLE — analyse at ~2-3 fps, never every frame.
 *   2. MOTION   — compare the current main-grid area against the last
 *      analysed/stable frame. A changed frame drives OCR (new content /
 *      vertical scroll); a stable frame (low motion) is what we want for a
 *      crisp crop.
 *
 * We use a 16×16 average-hash (phash) over the grid region as a cheap,
 * resolution-independent motion signal. Hamming distance between
 * consecutive grid hashes is the motion score.
 */
export interface FrameGateConfig {
  /** Minimum ms between analysed frames (throttle). ~333ms ≈ 3 fps. */
  minIntervalMs: number;
  /**
   * Hamming distance (0-256) above which the grid is considered "moving".
   * Below it the frame is "stable" — eligible for a crisp crop.
   */
  motionThreshold: number;
  /**
   * Hamming distance above which we consider the content genuinely CHANGED
   * (new rows scrolled in) and re-run heavy OCR even if we recently did.
   */
  changeThreshold: number;
}

export const DEFAULT_FRAME_GATE: FrameGateConfig = {
  minIntervalMs: 333,
  motionThreshold: 6,
  changeThreshold: 12,
};

export type FrameGateDecision = "analyze_changed" | "crop_stable" | "throttled" | "skip_no_change";

export interface FrameGateState {
  lastAnalyzedAt: number;
  lastHash: string | null;
  /** Hash of the last frame we treated as a stable crop target. */
  lastStableHash: string | null;
}

export function createFrameGateState(): FrameGateState {
  return { lastAnalyzedAt: 0, lastHash: null, lastStableHash: null };
}

/**
 * Compute a grid-region phash from a full-frame RGBA buffer. We hash only
 * the grid band (gridTop..bottomTrustLimit) so status-bar clocks and the
 * bottom carousel animation don't register as "motion".
 */
export function gridRegionHash(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  geo: DexGridGeometry = DEFAULT_DEX_GRID_GEOMETRY,
): string {
  const y0 = Math.floor(height * geo.gridTop);
  const y1 = Math.floor(height * geo.bottomTrustLimit);
  const x0 = Math.floor(width * geo.gridLeft);
  const x1 = Math.floor(width * geo.gridRight);
  const w = Math.max(1, x1 - x0);
  const h = Math.max(1, y1 - y0);
  // Extract the sub-rectangle into a tight RGBA buffer for phash.
  const sub = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const srcRow = ((y + y0) * width + x0) * 4;
    const dstRow = y * w * 4;
    sub.set(data.subarray(srcRow, srcRow + w * 4), dstRow);
  }
  return phash16FromRgba({ data: sub, width: w, height: h });
}

/**
 * Two-phase frame gate. Decides what to do with a frame given the last
 * analysed frame's hash + timestamp:
 *   - "throttled"        — too soon since the last analysis; skip.
 *   - "analyze_changed"  — content changed materially; run OCR + new dex
 *                          anchors.
 *   - "crop_stable"      — content is stable/low-motion; finalise crisp
 *                          crops for known registered cells.
 *   - "skip_no_change"   — identical paused frame already analysed; do
 *                          nothing (don't re-run heavy OCR).
 *
 * The caller updates `state` via `commitFrameGate` once it acts.
 */
export function decideFrameGate(
  state: FrameGateState,
  hash: string,
  now: number,
  cfg: FrameGateConfig = DEFAULT_FRAME_GATE,
): FrameGateDecision {
  if (now - state.lastAnalyzedAt < cfg.minIntervalMs) {
    return "throttled";
  }
  if (state.lastHash === null) {
    return "analyze_changed"; // first frame
  }
  const motion = phashHamming(hash, state.lastHash);
  if (motion >= cfg.changeThreshold) {
    return "analyze_changed";
  }
  if (motion <= cfg.motionThreshold) {
    // Stable. If we already treated this exact stable frame as a crop
    // target, there's nothing new to do.
    if (state.lastStableHash && phashHamming(hash, state.lastStableHash) <= cfg.motionThreshold) {
      return "skip_no_change";
    }
    return "crop_stable";
  }
  // Between thresholds: mild motion (still scrolling) — analyse to keep
  // dex anchors fresh but it's not a crop target yet.
  return "analyze_changed";
}

/** Record the outcome of acting on a frame so the next decision is correct. */
export function commitFrameGate(
  state: FrameGateState,
  hash: string,
  now: number,
  decision: FrameGateDecision,
): void {
  if (decision === "throttled" || decision === "skip_no_change") return;
  state.lastAnalyzedAt = now;
  state.lastHash = hash;
  if (decision === "crop_stable") {
    state.lastStableHash = hash;
  }
}

/**
 * Is a cell near the middle of the screen (vertically)? High-res crops are
 * only taken for cells near the centre after motion pauses, so the asset is
 * crisp and not edge-distorted.
 */
export function isCellNearMiddle(
  rect: DexCellRect,
  band = 0.22,
): boolean {
  return Math.abs(rect.cyPct - 0.5) <= band;
}

/**
 * Gate a crop: only crop a registered cell when the frame is stable AND the
 * cell is near the middle AND fully visible. Pure predicate so the live
 * loop and tests share one source of truth.
 */
export function shouldCaptureCrop(
  rect: DexCellRect,
  isRegistered: boolean,
  frameDecision: FrameGateDecision,
  geo: DexGridGeometry = DEFAULT_DEX_GRID_GEOMETRY,
): boolean {
  if (!isRegistered) return false;
  if (frameDecision !== "crop_stable") return false;
  if (!isCellFullyVisible(rect, geo)) return false;
  if (!isCellNearMiddle(rect)) return false;
  return true;
}

// ── Mean-colour helper (re-exported for tests/glue) ─────────────────────────

export { avgRgbFromRgba };
